/**
 * This is meant to be a replacement for Layout, as this one uses crehana-i18n.
 */
import React, { Component } from 'react';
import { ApolloProvider } from 'react-apollo';

import CUILayout from 'crehana-ui/Layout';
import {
  I18nProvider as CrehanaI18nProvider,
  I18nProviderProps,
} from 'crehana-i18n';

import RootErrorBoundaryWithSentry from 'Jsx/Shared/ErrorBoundaries/RootErrorBoundaryWithSentry';
import ProductsContainer from 'Jsx/ContextApis/Products';
import { AuthProvider } from 'Jsx/ContextApis/AuthContext';

import Channels from 'Jsx/Analytics/Channels';
import FacebookPixelInit from 'Jsx/Analytics/FacebookPixelInit';

import { ColorTheme } from 'Jsx/global.d.ts';

import createApolloClient from './ApolloClient';
import GlobalStyles from './GlobalStyles';
import UIThemeProvider from './UITheme/UIThemeProvider';

import { CookiesDisabledWarning } from './components';

export interface LayoutProps {
  /** Optional custom apollo client */
  client?: any;
  crehanaI18n?: I18nProviderProps;
  /**
   * crehana-ui Layout wrapper
   *
   * @default true
   */
  withCUI?: boolean;
  /**
   * Products context provider wrapper
   *
   * @default true
   */
  withSC?: boolean;
  /**
   * Enables styled components GlobalStyles to use the custom theme
   * from initial state
   */
  colorTheme?: ColorTheme;
  /**
   * Includes the UITheme provider (needed to handle light/dark mode).
   *
   * @default false
   */
  withUITheme?: boolean;
  /**
   * Controls if you want to send data to channels service
   */
  sendAnalyticsToChannels?: Boolean;
  /**
   * Controls if you want to send window.fbq('init', {})
   */
  sendFacebookPixelInit?: Boolean;
  /**
   * You should provide a single child to the Layout.
   */
  children: React.ReactChild;
}

class Layout extends Component<LayoutProps, {}> {
  validateFacebooKPixelInit = () => {
    const { sendFacebookPixelInit = true, children } = this.props;

    return sendFacebookPixelInit ? (
      <FacebookPixelInit>{children}</FacebookPixelInit>
    ) : (
      children
    );
  };

  validateAnalyticsChannels = () => {
    const { sendAnalyticsToChannels = true } = this.props;

    return sendAnalyticsToChannels ? (
      <Channels>{this.validateFacebooKPixelInit()}</Channels>
    ) : (
      this.validateFacebooKPixelInit()
    );
  };

  validateSC = () => {
    const { withSC = true, withUITheme } = this.props;

    return withSC ? (
      <ProductsContainer darkModeSupported={withUITheme}>
        {this.validateAnalyticsChannels()}
      </ProductsContainer>
    ) : (
      this.validateAnalyticsChannels()
    );
  };

  validateCUI = () => {
    const { withCUI = true } = this.props;

    return withCUI ? (
      <CUILayout>{this.validateSC()}</CUILayout>
    ) : (
      this.validateSC()
    );
  };

  validateI18N = () => {
    const { crehanaI18n } = this.props;

    if (crehanaI18n) {
      return (
        <CrehanaI18nProvider {...crehanaI18n}>
          {this.validateCUI()}
        </CrehanaI18nProvider>
      );
    }
    return this.validateCUI();
  };

  render() {
    const { client, colorTheme, withUITheme } = this.props;

    return (
      <React.Fragment>
        <CookiesDisabledWarning />
        <RootErrorBoundaryWithSentry>
          <ApolloProvider client={client || createApolloClient()}>
            <AuthProvider>
              {colorTheme ? <GlobalStyles colorTheme={colorTheme} /> : null}
              {withUITheme ? (
                <UIThemeProvider>{this.validateI18N()}</UIThemeProvider>
              ) : (
                this.validateI18N()
              )}
            </AuthProvider>
          </ApolloProvider>
        </RootErrorBoundaryWithSentry>
      </React.Fragment>
    );
  }
}

export default Layout;
